import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, { useMemo } from 'react'
import { useTsrClient } from './tsr'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Smart retry logic: only retry on network errors, not timeouts
      retry: (failureCount, error: unknown) => {
        const errorMessage =
          error instanceof Error ? error.message : String(error)
        // Never retry more than once
        if (failureCount >= 1) return false

        // Don't retry on timeout or connection pool errors
        if (errorMessage.includes('timeout')) return false
        if (errorMessage.includes('pool')) return false
        if (errorMessage.includes('503')) return false

        // Only retry on genuine network errors
        return (
          errorMessage.includes('network') || errorMessage.includes('fetch')
        )
      },

      // Increase stale time to reduce unnecessary refetches
      staleTime: 30000, // 30 seconds - data stays fresh for 30s

      // Disable automatic refetching on mount/focus for dashboard
      // This prevents unnecessary queries when switching tabs
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
})

export function Providers({ children }: { children: React.ReactNode }) {
  const tsrClient = useTsrClient()

  // Create a memoized version of the TSR client to prevent unnecessary re-renders
  const memoizedTsrClient = useMemo(() => tsrClient, [tsrClient])

  return (
    <QueryClientProvider client={queryClient}>
      <memoizedTsrClient.ReactQueryProvider>
        {children}
      </memoizedTsrClient.ReactQueryProvider>
    </QueryClientProvider>
  )
}
